import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { letterMetrics, getLetterWidth } from "@/data/letterSpacing";
import { useImagePreloader } from "@/hooks/useImagePreloader";
import type { Background } from "@/data/backgrounds";

interface PreviewCanvasProps {
  letters: { [key: string]: string };
  text: string;
  scale: number;
  isManualScale: boolean;
  aspectRatio: string;
  background: Background;
  shadowEnabled: boolean;
}

export interface PreviewCanvasRef {
  downloadHighRes: (resolution: number) => void;
}

export const PreviewCanvas = forwardRef<PreviewCanvasRef, PreviewCanvasProps>(
  ({ letters, text, scale, isManualScale, aspectRatio, background, shadowEnabled }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { loadedImages, isLoading } = useImagePreloader(letters);

  const renderBackground = (ctx: CanvasRenderingContext2D, width: number, height: number, bg: Background) => {
    if (bg.type === 'solid') {
      ctx.fillStyle = bg.colors[0];
      ctx.fillRect(0, 0, width, height);
    } else if (bg.type === 'linear') {
      const angle = (bg.angle || 0) * (Math.PI / 180);
      const x1 = width / 2 - Math.cos(angle) * Math.max(width, height) / 2;
      const y1 = height / 2 - Math.sin(angle) * Math.max(width, height) / 2;
      const x2 = width / 2 + Math.cos(angle) * Math.max(width, height) / 2;
      const y2 = height / 2 + Math.sin(angle) * Math.max(width, height) / 2;
      const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
      bg.colors.forEach((color, index) => {
        gradient.addColorStop(index / (bg.colors.length - 1), color);
      });
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else if (bg.type === 'radial') {
      const gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.max(width, height) / 2);
      bg.colors.forEach((color, index) => {
        gradient.addColorStop(index / (bg.colors.length - 1), color);
      });
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }
  };

  const applyEffects = (ctx: CanvasRenderingContext2D) => {
    if (shadowEnabled) {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 3;
      ctx.shadowOffsetY = 3;
    }
  };

  const clearEffects = (ctx: CanvasRenderingContext2D) => {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  };

  const getCanvasDimensions = () => {
    const baseSize = 600;
    switch (aspectRatio) {
      case "square":
        return { width: baseSize, height: baseSize };
      case "landscape":
        return { width: baseSize * 1.5, height: baseSize };
      case "portrait":
        return { width: baseSize, height: baseSize * 1.5 };
      case "wide":
        return { width: baseSize * 2, height: baseSize };
      default:
        return { width: baseSize, height: baseSize };
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = getCanvasDimensions();
    canvas.width = width;
    canvas.height = height;

    // Render background
    renderBackground(ctx, width, height, background);

    if (!text) return;

    const upperText = text.toUpperCase();
    const margin = 40;
    const maxLineWidth = width - (margin * 2);
    const maxHeight = height - (margin * 2);

    // Function to calculate layout with a given scale factor
    // Uses actual letter widths from font metrics for accurate spacing
    const calculateLayout = (scaleFactor: number) => {
      const letterSize = scaleFactor * 80;
      const spaceWidth = letterSize * 0.4; // Word spacing
      const lineHeight = letterSize * 1.4; // Sentence spacing
      const letterPadding = letterSize * 0.025; // 2.5% padding between letters

      // Split by newlines first to preserve manual line breaks
      const textLines = upperText.split('\n');
      const lines: Array<{ word: string; x: number }[]> = [];

      textLines.forEach((textLine) => {
        // Split each line by spaces to get words
        const words = textLine.split(/\s+/).filter(word => word.length > 0);
        let currentLine: Array<{ word: string; x: number }> = [];
        let currentLineWidth = 0;

        words.forEach((word) => {
          // Calculate word width using actual letter metrics plus padding
          let wordWidth = 0;
          for (let i = 0; i < word.length; i++) {
            wordWidth += getLetterWidth(word[i], scaleFactor);
            if (i < word.length - 1) {
              wordWidth += letterPadding; // Add padding between letters
            }
          }

          const widthWithSpace = currentLineWidth > 0 ? currentLineWidth + spaceWidth + wordWidth : wordWidth;

          if (widthWithSpace > maxLineWidth && currentLine.length > 0) {
            lines.push(currentLine);
            currentLine = [{ word, x: 0 }];
            currentLineWidth = wordWidth;
          } else {
            const xPos = currentLineWidth > 0 ? currentLineWidth + spaceWidth : 0;
            currentLine.push({ word, x: xPos });
            currentLineWidth = widthWithSpace;
          }
        });

        if (currentLine.length > 0) {
          lines.push(currentLine);
        } else if (textLine === '') {
          // Preserve empty lines (user pressed Enter without typing)
          lines.push([]);
        }
      });

      const totalHeight = lines.length * lineHeight;

      return { lines, letterSize, spaceWidth, lineHeight, totalHeight, letterPadding };
    };

    // Auto-scale logic: Use full auto-scale if not manual, otherwise respect user's scale
    // Get all words from all lines to find the longest word
    const allWords: string[] = [];
    upperText.split('\n').forEach((line) => {
      allWords.push(...line.split(/\s+/).filter(word => word.length > 0));
    });
    const longestWord = allWords.length > 0 ? allWords.reduce((a, b) => a.length > b.length ? a : b, '') : '';

    let effectiveScale: number;
    let layout;

    if (!isManualScale) {
      // AUTO-SCALE MODE: Calculate maximum possible scale that fits
      // Calculate actual longest word width using real letter metrics
      let actualLongestWordWidth = 0;
      for (let i = 0; i < longestWord.length; i++) {
        actualLongestWordWidth += getLetterWidth(longestWord[i], 1); // Use scale 1 for base width
      }
      // Scale should give us: actualLongestWordWidth * scale < maxLineWidth
      const maxScaleForWidth = maxLineWidth / actualLongestWordWidth;

      // Start with width constraint
      let testScale = maxScaleForWidth;
      layout = calculateLayout(testScale);

      // If height doesn't fit, reduce scale iteratively
      while (layout.totalHeight > maxHeight && testScale > 0.1) {
        testScale *= 0.95; // Reduce by 5% each iteration
        layout = calculateLayout(testScale);
      }

      effectiveScale = testScale;
    } else {
      // MANUAL MODE: Start with user's scale, shrink only if needed
      effectiveScale = scale / 100;
      layout = calculateLayout(effectiveScale);

      // Calculate if content fits at current scale
      let longestWordWidth = 0;
      for (let i = 0; i < longestWord.length; i++) {
        longestWordWidth += getLetterWidth(longestWord[i], effectiveScale);
      }

      // If content doesn't fit, shrink to fit
      if (longestWordWidth > maxLineWidth || layout.totalHeight > maxHeight) {
        // Calculate actual longest word width using real letter metrics
        let actualLongestWordWidth = 0;
        for (let i = 0; i < longestWord.length; i++) {
          actualLongestWordWidth += getLetterWidth(longestWord[i], 1); // Use scale 1 for base width
        }
        const maxScaleForWidth = maxLineWidth / actualLongestWordWidth;
        let testScale = Math.min(maxScaleForWidth, effectiveScale);
        layout = calculateLayout(testScale);

        while (layout.totalHeight > maxHeight && testScale > 0.1) {
          testScale *= 0.95;
          layout = calculateLayout(testScale);
        }

        effectiveScale = testScale;
        layout = calculateLayout(effectiveScale);
      }
    }

    // Phase 2: Render letters using actual letter metrics for spacing
    const { lines, letterSize, spaceWidth, lineHeight, totalHeight, letterPadding } = layout;
    // Center the text block: vertically center all lines as a group
    // Each line's yPos is at: startY + lineIndex * lineHeight
    // Letters are drawn with top at: yPos - letterSize/2 and bottom at: yPos + letterSize/2
    // So the block spans vertically from: startY - letterSize/2 to: (startY + (lines.length-1)*lineHeight) + letterSize/2
    // To perfectly center this block in the canvas: center point should be at height/2
    const blockTopY = -letterSize / 2;  // relative to startY
    const blockBottomY = (lines.length - 1) * lineHeight + letterSize / 2;  // relative to startY
    const blockCenterY = (blockTopY + blockBottomY) / 2;  // relative to startY
    const startY = height / 2 - blockCenterY;

    lines.forEach((line, lineIndex) => {
      // Calculate line width for centering using actual letter widths
      const lineWidth = line.reduce((total, item, idx) => {
        let wordWidth = 0;
        for (let i = 0; i < item.word.length; i++) {
          wordWidth += getLetterWidth(item.word[i], effectiveScale);
          if (i < item.word.length - 1) {
            wordWidth += letterPadding; // Add padding between letters
          }
        }
        return total + wordWidth + (idx > 0 ? spaceWidth : 0);
      }, 0);
      // Center horizontally within the margin-constrained area
      const centerX = (width - margin * 2) / 2 + margin;
      let lineStartX = centerX - lineWidth / 2;
      // Ensure line doesn't go past left margin
      if (lineStartX < margin) {
        lineStartX = margin;
      }
      // Ensure line doesn't go past right margin
      if (lineStartX + lineWidth > width - margin) {
        lineStartX = width - margin - lineWidth;
      }
      const yPos = startY + lineIndex * lineHeight;

      line.forEach((item) => {
        let wordStartX = lineStartX + item.x;

        item.word.split("").forEach((char, charIndex) => {
          const letterWidth = getLetterWidth(char, effectiveScale);
          const letterImg = loadedImages[char];

          if (!letterImg) {
            // Draw placeholder
            ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
            ctx.fillRect(wordStartX, yPos - letterSize / 2, letterWidth, letterSize);
            ctx.fillStyle = "#999";
            ctx.font = `${letterSize / 2}px Inter`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(char, wordStartX + letterWidth / 2, yPos);
            wordStartX += letterWidth;
            // Add padding after each letter except the last one in the word
            if (charIndex < item.word.length - 1) {
              wordStartX += letterPadding;
            }
            return;
          }

          // Apply shadow and draw the main letter
          applyEffects(ctx);
          ctx.drawImage(letterImg, wordStartX, yPos - letterSize / 2, letterWidth, letterSize);
          clearEffects(ctx);

          wordStartX += letterWidth;
          // Add padding after each letter except the last one in the word
          if (charIndex < item.word.length - 1) {
            wordStartX += letterPadding;
          }
        });
      });
    });
  }, [loadedImages, text, scale, isManualScale, aspectRatio, background, shadowEnabled]);

  // Expose download function to parent component
  useImperativeHandle(ref, () => ({
    downloadHighRes: (resolutionMultiplier: number = 3) => {
      if (!text) return;

      const { width, height } = getCanvasDimensions();
      const highResCanvas = document.createElement('canvas');
      const highResWidth = width * resolutionMultiplier;
      const highResHeight = height * resolutionMultiplier;

      highResCanvas.width = highResWidth;
      highResCanvas.height = highResHeight;

      const ctx = highResCanvas.getContext('2d');
      if (!ctx) return;

      // Render at high resolution
      const renderHighRes = () => {
        // Render background
        renderBackground(ctx, highResWidth, highResHeight, background);

        const upperText = text.toUpperCase();
        const margin = 40 * resolutionMultiplier;
        const maxLineWidth = highResWidth - (margin * 2);
        const maxHeight = highResHeight - (margin * 2);

        // FIX 3: Keep calculateLayout in base resolution (without resolutionMultiplier)
        // This matches the preview's calculateLayout and fixes resolution mixing bugs
        const calculateLayout = (scaleFactor: number) => {
          const letterSize = scaleFactor * 80;  // Base resolution
          const spaceWidth = letterSize * 0.4;
          const lineHeight = letterSize * 1.4;
          const letterPadding = letterSize * 0.025;  // Base resolution

          // Split by newlines first to preserve manual line breaks
          const textLines = upperText.split('\n');
          const lines: Array<{ word: string; x: number }[]> = [];

          textLines.forEach((textLine) => {
            // Split each line by spaces to get words
            const words = textLine.split(/\s+/).filter(word => word.length > 0);
            let currentLine: Array<{ word: string; x: number }> = [];
            let currentLineWidth = 0;

            words.forEach((word) => {
              // Calculate word width in base resolution
              let wordWidth = 0;
              for (let i = 0; i < word.length; i++) {
                wordWidth += getLetterWidth(word[i], scaleFactor);  // Base resolution
                if (i < word.length - 1) {
                  wordWidth += letterPadding;  // Base resolution padding
                }
              }

              // Calculate max line width in base resolution for fair comparison
              const maxLineWidthBase = maxLineWidth / resolutionMultiplier;
              const widthWithSpace = currentLineWidth > 0 ? currentLineWidth + spaceWidth + wordWidth : wordWidth;

              if (widthWithSpace > maxLineWidthBase && currentLine.length > 0) {
                lines.push(currentLine);
                currentLine = [{ word, x: 0 }];
                currentLineWidth = wordWidth;
              } else {
                const xPos = currentLineWidth > 0 ? currentLineWidth + spaceWidth : 0;
                currentLine.push({ word, x: xPos });
                currentLineWidth = widthWithSpace;
              }
            });

            if (currentLine.length > 0) {
              lines.push(currentLine);
            } else if (textLine === '') {
              // Preserve empty lines (user pressed Enter without typing)
              lines.push([]);
            }
          });

          const totalHeight = lines.length * lineHeight;
          return { lines, letterSize, spaceWidth, lineHeight, totalHeight, letterPadding };
        };

        // FIX 1: Add isManualScale support to match preview behavior
        // Get all words from all lines to find the longest word
        const allWords: string[] = [];
        upperText.split('\n').forEach((line) => {
          allWords.push(...line.split(/\s+/).filter(word => word.length > 0));
        });
        const longestWord = allWords.length > 0 ? allWords.reduce((a, b) => a.length > b.length ? a : b, '') : '';

        let effectiveScale: number;
        let layout;

        if (!isManualScale) {
          // FIX 1: AUTO-SCALE MODE - Calculate maximum possible scale that fits
          // Calculate actual longest word width using real letter metrics
          let actualLongestWordWidth = 0;
          for (let i = 0; i < longestWord.length; i++) {
            actualLongestWordWidth += getLetterWidth(longestWord[i], 1); // Use scale 1 for base width
          }
          const maxScaleForWidth = (maxLineWidth / resolutionMultiplier) / actualLongestWordWidth;

          let testScale = maxScaleForWidth;
          layout = calculateLayout(testScale);

          // FIX 5: Add iterative scale testing with while loop
          const maxHeightBase = maxHeight / resolutionMultiplier;
          while (layout.totalHeight > maxHeightBase && testScale > 0.1) {
            testScale *= 0.95;
            layout = calculateLayout(testScale);
          }

          effectiveScale = testScale;
        } else {
          // FIX 1: MANUAL MODE - Start with user's scale, shrink only if needed
          effectiveScale = scale / 100;
          layout = calculateLayout(effectiveScale);

          // FIX 2: Calculate longest word width in base resolution for proper comparison
          let longestWordWidth = 0;
          for (let i = 0; i < longestWord.length; i++) {
            longestWordWidth += getLetterWidth(longestWord[i], effectiveScale);
          }

          const maxLineWidthBase = maxLineWidth / resolutionMultiplier;
          const maxHeightBase = maxHeight / resolutionMultiplier;

          // If content doesn't fit, shrink to fit
          if (longestWordWidth > maxLineWidthBase || layout.totalHeight > maxHeightBase) {
            // Calculate actual longest word width using real letter metrics
            let actualLongestWordWidth = 0;
            for (let i = 0; i < longestWord.length; i++) {
              actualLongestWordWidth += getLetterWidth(longestWord[i], 1); // Use scale 1 for base width
            }
            const maxScaleForWidth = maxLineWidthBase / actualLongestWordWidth;
            let testScale = Math.min(maxScaleForWidth, effectiveScale);
            layout = calculateLayout(testScale);

            // FIX 5: Add iterative scale testing with while loop
            while (layout.totalHeight > maxHeightBase && testScale > 0.1) {
              testScale *= 0.95;
              layout = calculateLayout(testScale);
            }

            effectiveScale = testScale;
            layout = calculateLayout(effectiveScale);
          }
        }

        // Now scale up everything for high-resolution rendering
        const { lines, letterSize, spaceWidth, lineHeight, totalHeight, letterPadding } = layout;
        const letterSizeHighRes = letterSize * resolutionMultiplier;
        const spaceWidthHighRes = spaceWidth * resolutionMultiplier;
        const lineHeightHighRes = lineHeight * resolutionMultiplier;
        const totalHeightHighRes = totalHeight * resolutionMultiplier;
        const letterPaddingHighRes = letterPadding * resolutionMultiplier;

        // Center the text block with same logic as preview:
        // Account for letters being drawn from yPos - letterSizeHighRes/2
        const blockTopYHighRes = -letterSizeHighRes / 2;
        const blockBottomYHighRes = (lines.length - 1) * lineHeightHighRes + letterSizeHighRes / 2;
        const blockCenterYHighRes = (blockTopYHighRes + blockBottomYHighRes) / 2;
        const startY = highResHeight / 2 - blockCenterYHighRes;

        lines.forEach((line, lineIndex) => {
          // Calculate line width in base resolution first
          const lineWidthBase = line.reduce((total, item, idx) => {
            let wordWidth = 0;
            for (let i = 0; i < item.word.length; i++) {
              wordWidth += getLetterWidth(item.word[i], effectiveScale);
              if (i < item.word.length - 1) {
                wordWidth += letterPadding;
              }
            }
            return total + wordWidth + (idx > 0 ? spaceWidth : 0);
          }, 0);

          // Scale up for high-res rendering
          const lineWidth = lineWidthBase * resolutionMultiplier;
          // Center horizontally within the margin-constrained area (high-res)
          const marginHighRes = 40 * resolutionMultiplier;
          const centerX = (highResWidth - marginHighRes * 2) / 2 + marginHighRes;
          let lineStartX = centerX - lineWidth / 2;
          // Ensure line doesn't go past left margin
          if (lineStartX < marginHighRes) {
            lineStartX = marginHighRes;
          }
          // Ensure line doesn't go past right margin
          if (lineStartX + lineWidth > highResWidth - marginHighRes) {
            lineStartX = highResWidth - marginHighRes - lineWidth;
          }
          const yPos = startY + lineIndex * lineHeightHighRes;

          line.forEach((item) => {
            let wordStartX = lineStartX + (item.x * resolutionMultiplier);

            item.word.split("").forEach((char, charIndex) => {
              // FIX 4: Calculate letter width properly - multiply base width by resolution
              const letterWidthBase = getLetterWidth(char, effectiveScale);
              const letterWidth = letterWidthBase * resolutionMultiplier;
              const letterImg = loadedImages[char];

              if (!letterImg) {
                ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
                ctx.fillRect(wordStartX, yPos - letterSizeHighRes / 2, letterWidth, letterSizeHighRes);
                ctx.fillStyle = "#999";
                ctx.font = `${letterSizeHighRes / 2}px Inter`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText(char, wordStartX + letterWidth / 2, yPos);
                wordStartX += letterWidth;
                if (charIndex < item.word.length - 1) {
                  wordStartX += letterPaddingHighRes;
                }
                return;
              }

              // Apply shadow and draw the main letter at high resolution
              if (shadowEnabled) {
                ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
                ctx.shadowBlur = 8 * resolutionMultiplier;
                ctx.shadowOffsetX = 3 * resolutionMultiplier;
                ctx.shadowOffsetY = 3 * resolutionMultiplier;
              }
              ctx.drawImage(letterImg, wordStartX, yPos - letterSizeHighRes / 2, letterWidth, letterSizeHighRes);
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;

              wordStartX += letterWidth;
              if (charIndex < item.word.length - 1) {
                wordStartX += letterPaddingHighRes;
              }
            });
          });
        });

        // All images are preloaded, so trigger download immediately
        highResCanvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `earth-rights-${Date.now()}.png`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
          }
        }, 'image/png');
      };

      renderHighRes();
    }
  }));

  const { width, height } = getCanvasDimensions();

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center" style={{ height: height }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading letters...</p>
          </div>
        </div>
      ) : (
        <div className="flex justify-center overflow-auto">
          <canvas
            ref={canvasRef}
            className="transition-all duration-300 rounded-[var(--radius)]"
            style={{
              maxWidth: "100%",
              height: "auto",
              border: "1px solid hsl(var(--border) / 0.5)",
            }}
          />
        </div>
      )}
    </>
  );
});

PreviewCanvas.displayName = 'PreviewCanvas';
